import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { EmojiEvents as TrophyIcon } from "@mui/icons-material";
import { useOrganization } from "../contexts/OrganizationContext";
import { getOrgGradient } from "../utils/orgGradient";
import {
  TournamentPartnerStat,
} from "../hooks/useTournamentStats";

interface TournamentPartnerStatsProps {
  partners: TournamentPartnerStat[];
  bestPartner: TournamentPartnerStat | null;
}

const TournamentPartnerStats: React.FC<TournamentPartnerStatsProps> = ({
  partners,
  bestPartner,
}) => {
  const theme = useTheme();
  const { currentOrg } = useOrganization();
  const gradient = getOrgGradient(currentOrg?.slug);

  if (partners.length === 0) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Partners
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No partner data yet — results will appear after completed tournament matches.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Partners
        </Typography>

        {bestPartner && (
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: currentOrg?.slug === "padel-eagle" ? "#743444" : gradient,
              color: "white",
              boxShadow: currentOrg?.slug === "padel-eagle" ? "none" : 2,
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: alpha("#fff", 0.2),
                color: "white",
                fontWeight: "bold",
              }}
            >
              <TrophyIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Best Partner
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }} noWrap>
                {bestPartner.partnerName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {Math.round(bestPartner.winRate * 100)}% win rate · {bestPartner.matchesWon}W-
                {bestPartner.matchesPlayed - bestPartner.matchesWon}L in {bestPartner.matchesPlayed} matches
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {partners.map((partner) => {
            const winPct = Math.round(partner.winRate * 100);
            const barColor =
              winPct >= 60
                ? theme.palette.success.main
                : winPct >= 40
                ? theme.palette.warning.main
                : theme.palette.error.main;

            return (
              <Box
                key={partner.partnerId}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: 2 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: "0.9rem",
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: "bold",
                      }}
                    >
                      {partner.partnerName?.charAt(0) || "?"}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {partner.partnerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {partner.tournamentsPlayed} tournament{partner.tournamentsPlayed !== 1 ? "s" : ""} ·{" "}
                        {partner.matchesPlayed} match{partner.matchesPlayed !== 1 ? "es" : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: "bold", color: barColor }}>
                      {winPct}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {partner.matchesWon}W-{partner.matchesPlayed - partner.matchesWon}L
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={winPct}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(barColor, 0.15),
                    "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TournamentPartnerStats;
